import { ClientEvents, ReferralShareLocation, ShareType } from 'common/events/ClientEvents';
import { formatCurrency } from 'common/FormatUtilities';
import { formatTotalValue } from 'common/ReferTextUtilities';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import MoneyDish from 'src/assets/svg/money_dish.svg';
import { isNativeMobileApp } from 'src/mobile-app/isNativeMobileApp';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useLocation } from 'src/shared/hooks/useLocation';
import { useShare } from 'src/shared/hooks/useShare';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { getReferralUrl } from 'src/shared/utils/PromoUtilities';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';

export const ReferralBannerV2 = () => {
  const isMobileApp = isNativeMobileApp();
  const tracker = useTracker();
  const [currentUser] = useCurrentUser();
  const { isSmallScreen } = useScreenContext();
  const referralCode = currentUser?.referralCode?.code ?? '';
  const { getValue, giveValue } = currentUser?.referralCode ?? { value: null, giveValue: 0 };
  const giveText = formatTotalValue({ giveValue, maxUsesPerUser: currentUser?.referralCode?.maxUsesPerUser ?? 1 });
  const getText = formatCurrency(getValue, true);
  const referralTextGive = giveValue ? `Share ${giveText} in Shef credits,` : '';
  const referralTextGet = getValue ? `get ${getText} off your next order!` : '';
  const referralShareMessage = getValue
    ? `Get ${giveText} off! You'll receive ${getText} off your first 2 orders using my referral link.`
    : '';

  const referralLink = getReferralUrl({ code: referralCode });
  const [copyText, setCopyText] = useState<React.ReactNode>(isMobileApp ? 'Share link' : 'Copy link');
  const location = useLocation();

  const onShare = (type: ShareType) => {
    tracker.track(ClientEvents.REFERRAL_CODE_SHARED, {
      code: referralCode,
      type,
      location:
        location.pathname === Routes.CONSUMER_MEAL_PLANS_MANAGE
          ? ReferralShareLocation.MANAGE_MEAL_PLAN
          : ReferralShareLocation.UPCOMING_MEAL_PLAN_ORDERS,
    });
    if (type !== ShareType.NATIVE) {
      setCopyText('Link copied');
      setTimeout(() => {
        setCopyText('Copy link');
      }, 2000);
    }
  };

  const { share } = useShare({
    url: referralLink,
    onShare,
    onError: () => null,
    shareMessage: referralShareMessage,
  });

  if (isEmpty(referralCode)) {
    return null;
  }

  return (
    <div className='flex w-full flex-row items-start justify-center rounded-lg bg-mint-100 p-4 shadow-navigation-drop-top md:gap-8'>
      <div className='flex w-full flex-col items-start gap-4'>
        <div className='flex-start flex w-full gap-4'>
          <div className='flex-start flex w-full flex-col gap-2'>
            <div className='w-full text-lg font-bold leading-8'>
              {referralTextGive} <br className='md:hidden' />
              {referralTextGet}
            </div>
            <div>
              Earn unlimited credits!{' '}
              <a className='cursor-pointer underline' href={Routes.CONSUMER_ACCOUNT_SHARE}>
                Learn more.
              </a>
            </div>
          </div>
          <img src={MoneyDish} alt={'Rewards'} className='size-16 object-contain md:hidden' />
        </div>
        <Button buttonType={ButtonType.Secondary} align={'center'} onClick={share}>
          {copyText}
        </Button>
      </div>
      {!isSmallScreen && (
        <div className='flex items-center justify-center'>
          <img src={MoneyDish} alt={'Rewards'} className='z-40 size-20 md:size-28' />
        </div>
      )}
    </div>
  );
};
